import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowRightLeft, 
  RefreshCw, 
  Edit3, 
  Check,
  X,
  Loader2,
  DollarSign,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

// Common currencies
const CURRENCIES = {
  USD: { symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  EUR: { symbol: '€', name: 'Euro', flag: '🇪🇺' },
  GBP: { symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  NGN: { symbol: '₦', name: 'Nigerian Naira', flag: '🇳🇬' },
  GHS: { symbol: '₵', name: 'Ghanaian Cedi', flag: '🇬🇭' },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', flag: '🇰🇪' },
  ZAR: { symbol: 'R', name: 'South African Rand', flag: '🇿🇦' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  JPY: { symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳' },
  INR: { symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
};

/**
 * FX Rate Picker Component
 * 
 * Allows users to:
 * - Select a currency for transactions
 * - View live exchange rates
 * - Override with custom rate
 * - See converted amounts in real-time
 * 
 * Props:
 * - baseCurrency: The business's base currency (e.g., 'NGN')
 * - selectedCurrency: Currently selected foreign currency
 * - onCurrencyChange: Callback when currency changes
 * - fxRate: Current exchange rate
 * - onRateChange: Callback when rate changes (including overrides)
 * - amount: Transaction amount in selected currency
 * - showConversion: Whether to show the converted amount
 * - disabled: Whether the picker is disabled
 * - compact: Use compact mode (inline)
 */
const FXRatePicker = ({
  baseCurrency = 'NGN',
  selectedCurrency,
  onCurrencyChange,
  fxRate,
  onRateChange,
  amount = 0,
  showConversion = true,
  disabled = false,
  compact = false,
  label = 'Invoice Currency',
  rateDate = null,
  // iter971k-Z · Turn 5d - allow parent (e.g. Edit/Duplicate flows) to seed
  // the internal rateSource so a hydrated manual rate is NOT overwritten
  // by an automatic live-rate refetch on mount.
  initialRateSource = 'live',
  // Currency is derived elsewhere (e.g. from the paying bank account) - only the rate is editable
  lockCurrency = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [isOverriding, setIsOverriding] = useState(false);
  const [overrideRate, setOverrideRate] = useState('');
  const [liveRate, setLiveRate] = useState(null);
  const [rateSource, setRateSource] = useState(initialRateSource);
  const [rateDateUsed, setRateDateUsed] = useState(null);
  // iter 1015 - full platform currency list (341 currencies from daily store)
  const [allCurrencies, setAllCurrencies] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/fx/currencies/all`)
      .then((r) => {
        const list = r.data?.currencies;
        if (live && Array.isArray(list) && list.length) setAllCurrencies(list);
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const currencyEntries = React.useMemo(() => {
    const common = Object.entries(CURRENCIES);
    if (!allCurrencies) return common;
    const commonSet = new Set(Object.keys(CURRENCIES));
    const rest = allCurrencies
      .filter((c) => c.code && !commonSet.has(c.code))
      .map((c) => [c.code, { name: c.name || c.code, flag: '', symbol: c.code }]);
    return [...common, ...rest];
  }, [allCurrencies]);

  // iter971k-Z · Turn 5d - Sync internal rateSource when the parent
  // hydrates the initialRateSource asynchronously (e.g. after fetching an
  // existing invoice/bill in edit mode). Without this, the mount race with
  // fetchLiveRate() overwrites the hydrated manual fxRate with the live one.
  useEffect(() => {
    if (initialRateSource && initialRateSource !== rateSource) {
      setRateSource(initialRateSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRateSource]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  
  // Track which currencies we've already shown toasts for (to prevent spam)
  const shownToastsRef = React.useRef(new Set());
  
  // Fetch live rate when currency changes
  const fetchLiveRate = useCallback(async (currency, showToast = false, forceToast = false) => {
    if (!currency || currency === baseCurrency) {
      onRateChange?.(1);
      setLiveRate(1);
      return;
    }
    
    setLoading(true);
    try {
      // iter 1014 - date-aware: honor the DOCUMENT date when provided; falls
      // back to today's smart rate otherwise.
      let rate;
      if (rateDate) {
        const response = await axios.get(`${API}/fx/rate-for-date/${currency}/${baseCurrency}`, { params: { date: rateDate } });
        rate = response.data?.rate;
        setRateDateUsed(response.data?.rate_date || null);
      } else {
        const response = await axios.get(`${API}/fx/rates/smart/${currency}/${baseCurrency}?max_age_hours=24`);
        rate = response.data?.rate;
        setRateDateUsed(null);
      }
      
      if (rate) {
        // The rate is already "1 foreignCurrency = X baseCurrency"
        setLiveRate(rate);
        
        // Only update if not using override AND parent didn't ask us to
        // preserve a manual/hydrated rate.  Guards against the mount race
        // where rateSource captured in the closure lags behind the prop.
        if (rateSource === 'live' && initialRateSource !== 'manual') {
          onRateChange?.(rate);
          
          // Show toast if:
          // 1. forceToast is true (refresh button clicked)
          // 2. OR showToast is true AND we haven't shown it for this currency yet (first selection)
          const toastKey = `${currency}-${baseCurrency}`;
          if (forceToast || (showToast && !shownToastsRef.current.has(toastKey))) {
            if (showToast) {
              shownToastsRef.current.add(toastKey);
            }
            toast.success(`Live rate fetched: 1 ${currency} = ${rate.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${baseCurrency}`);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch FX rate:', error);
      toast.error('Failed to fetch exchange rate');
    } finally {
      setLoading(false);
    }
  }, [baseCurrency, onRateChange, rateSource, initialRateSource, rateDate]);
  
  // Fetch rate when currency changes (silently, no toast)
  useEffect(() => {
    if (selectedCurrency && selectedCurrency !== baseCurrency) {
      fetchLiveRate(selectedCurrency, false);
    }
  }, [selectedCurrency, baseCurrency, fetchLiveRate]);
  
  // Handle currency selection (user action - show toast once)
  const handleCurrencyChange = (currency) => {
    if (!currency) return; // Radix hidden <select> bubbles "" on programmatic value changes
    onCurrencyChange?.(currency);
    setIsOverriding(false);
    setRateSource('live');
    setOverrideRate('');
    setRateWarning(null);
    
    // If switching to a foreign currency, fetch rate and show toast (once)
    if (currency && currency !== baseCurrency) {
      fetchLiveRate(currency, true);
    }
  };
  
  // Rate variance validation
  const [rateWarning, setRateWarning] = useState(null);
  const VARIANCE_THRESHOLD = 0.05; // 5% variance threshold
  
  const validateRateVariance = (customRate, marketRate) => {
    if (!marketRate || !customRate) return null;
    
    const variance = Math.abs((customRate - marketRate) / marketRate);
    const variancePercent = (variance * 100).toFixed(1);
    
    if (variance > 0.10) {
      // >10% variance - High warning
      return {
        level: 'high',
        message: `Rate differs by ${variancePercent}% from market rate. Please verify.`,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      };
    } else if (variance > VARIANCE_THRESHOLD) {
      // >5% variance - Medium warning
      return {
        level: 'medium',
        message: `Rate differs by ${variancePercent}% from market rate.`,
        color: 'text-amber-600 dark:text-amber-400',
        bgColor: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
      };
    }
    return null;
  };
  
  // Handle rate override with validation
  const applyOverride = () => {
    const rate = parseFloat(overrideRate);
    if (!rate || rate <= 0) {
      toast.error('Please enter a valid rate');
      return;
    }
    
    // Validate variance
    const warning = validateRateVariance(rate, liveRate);
    setRateWarning(warning);
    
    if (warning?.level === 'high') {
      // Show confirmation for high variance
      if (!window.confirm(`This rate differs by more than 10% from the market rate (${liveRate?.toLocaleString(undefined, {maximumFractionDigits: 2})}). Are you sure you want to apply this rate?`)) {
        return;
      }
    }
    
    onRateChange?.(rate);
    setRateSource('manual');
    setIsOverriding(false);
    
    if (warning) {
      toast.warning(`Custom rate applied with ${warning.level} variance warning`);
    } else {
      toast.success('Custom rate applied');
    }
  };
  
  // Revert to live rate
  const revertToLive = () => {
    if (liveRate) {
      onRateChange?.(liveRate);
      setRateSource('live');
      setOverrideRate('');
      setRateWarning(null);
      toast.success('Reverted to live rate');
    }
  };
  
  // Real-time variance check while typing
  const handleOverrideChange = (value) => {
    setOverrideRate(value);
    const rate = parseFloat(value);
    if (rate && liveRate) {
      const warning = validateRateVariance(rate, liveRate);
      setRateWarning(warning);
    } else {
      setRateWarning(null);
    }
  };
  
  // Calculate converted amount
  const convertedAmount = amount && fxRate ? amount * fxRate : 0;
  
  // Is foreign currency selected?
  const isForeignCurrency = selectedCurrency && selectedCurrency !== baseCurrency;
  
  // Compact inline mode
  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Select
          value={selectedCurrency || baseCurrency}
          onValueChange={handleCurrencyChange}
          disabled={disabled || lockCurrency}
        >
          <SelectTrigger className="w-24 h-8" data-testid="fx-currency-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {currencyEntries.map(([code, info]) => (
              <SelectItem key={code} value={code}>
                {info.flag} {code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {isForeignCurrency && (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" disabled={disabled}>
                {loading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <ArrowRightLeft className="w-3 h-3" />
                    {fxRate?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    {rateSource === 'manual' && (
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-1">Custom</Badge>
                    )}
                  </>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">1 {selectedCurrency} =</span>
                  <span className="font-mono font-medium">{fxRate?.toLocaleString(undefined, { maximumFractionDigits: 4 })} {baseCurrency}</span>
                </div>
                
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.0001"
                    placeholder="Custom rate"
                    value={overrideRate}
                    onChange={(e) => handleOverrideChange(e.target.value)}
                    className={`h-8 text-sm ${rateWarning ? 'border-amber-400' : ''}`}
                  />
                  <Button size="sm" className="h-8" onClick={applyOverride}>
                    <Check className="w-3 h-3" />
                  </Button>
                </div>
                
                {rateWarning && (
                  <div className={`text-xs p-2 rounded ${rateWarning.bgColor} ${rateWarning.color}`}>
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    {rateWarning.message}
                  </div>
                )}
                
                {rateSource === 'manual' && liveRate && (
                  <Button variant="ghost" size="sm" className="w-full h-7 text-xs" onClick={revertToLive}>
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Revert to live ({liveRate.toLocaleString(undefined, { maximumFractionDigits: 2 })})
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  }
  
  // Full mode
  return (
    <div className="space-y-3 p-4 border rounded-lg bg-slate-50/50 dark:bg-slate-800/50">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-500" />
          {label}
        </Label>
        {isForeignCurrency && (
          <Badge 
            variant={rateSource === 'manual' ? 'secondary' : 'outline'} 
            className="text-xs"
          >
            {rateSource === 'manual' ? 'Custom Rate' : 'Live Rate'}
          </Badge>
        )}
      </div>
      
      <div className="flex gap-3">
        <Select
          value={selectedCurrency || baseCurrency}
          onValueChange={handleCurrencyChange}
          disabled={disabled}
        >
          <SelectTrigger className="flex-1" data-testid="currency-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {currencyEntries.map(([code, info]) => (
              <SelectItem key={code} value={code}>
                <div className="flex items-center gap-2">
                  {info.flag && <span>{info.flag}</span>}
                  <span>{code}</span>
                  <span className="text-slate-500 text-sm">- {info.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {isForeignCurrency && (
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => fetchLiveRate(selectedCurrency, false, true)}
            disabled={loading}
            title="Refresh exchange rate"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>
      
      {isForeignCurrency && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg border">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600 dark:text-slate-400">
                1 {selectedCurrency} =
              </span>
            </div>
            
            {isOverriding ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="Enter rate"
                  value={overrideRate}
                  onChange={(e) => handleOverrideChange(e.target.value)}
                  className={`w-32 h-8 text-right font-mono ${rateWarning ? 'border-amber-400 focus:border-amber-500' : ''}`}
                  autoFocus
                />
                <span className="text-sm text-slate-500">{baseCurrency}</span>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={applyOverride}>
                  <Check className="w-4 h-4 text-emerald-500" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setIsOverriding(false); setRateWarning(null); }}>
                  <X className="w-4 h-4 text-slate-400" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                ) : (
                  <>
                    <span className="font-mono font-semibold text-lg">
                      {fxRate?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                    </span>
                    <span className="text-sm text-slate-500">{baseCurrency}</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0 ml-2"
                      onClick={() => {
                        setIsOverriding(true);
                        setOverrideRate(fxRate?.toString() || '');
                        setRateWarning(null);
                      }}
                      disabled={disabled}
                    >
                      <Edit3 className="w-4 h-4 text-slate-400 hover:text-emerald-500" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Rate Variance Warning */}
          {rateDate && rateDateUsed && rateSource !== 'manual' && (
            <p className="text-xs text-slate-500 dark:text-slate-400 px-1" data-testid="fx-rate-date-note">
              Rate as of {rateDateUsed}{rateDateUsed !== rateDate ? ` (nearest available to ${rateDate})` : ''}. Follows the document date.
            </p>
          )}

          {rateWarning && (
            <div className={`flex items-start gap-2 p-3 rounded-lg border ${rateWarning.bgColor}`}>
              <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${rateWarning.color}`} />
              <div className={`text-sm ${rateWarning.color}`}>
                <p className="font-medium">{rateWarning.level === 'high' ? 'High Variance Detected' : 'Rate Variance'}</p>
                <p className="text-xs opacity-80">{rateWarning.message}</p>
              </div>
            </div>
          )}
          
          {rateSource === 'manual' && liveRate && !rateWarning && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Live rate: {liveRate.toLocaleString(undefined, { maximumFractionDigits: 2 })} {baseCurrency}
              </span>
              <Button 
                variant="link" 
                size="sm" 
                className="h-auto p-0 text-xs"
                onClick={revertToLive}
              >
                Use live rate
              </Button>
            </div>
          )}
          
          {showConversion && amount > 0 && (
            <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <span className="text-sm text-emerald-700 dark:text-emerald-400">
                Base Currency Equivalent
              </span>
              <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                {CURRENCIES[baseCurrency]?.symbol}{convertedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {baseCurrency}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FXRatePicker;
